// Workspace enumeration for the account switcher (CONSOLE-ACCOUNT-SWITCHER-LIVE). The pure,
// IdP-agnostic contract (MyAccount, MY_ACCOUNTS_COLUMNS, fetchMyAccounts, resolution, storage key)
// now lives in @secops/shared so console and mobile resolve tenancy identically (PRD-001K K1).
// Console keeps its own localStorage persistence here and passes the stored value into the pure
// resolver; the runtime-config accountId remains the default/fallback when the view is empty.

import {
  ACCOUNT_SELECTION_KEY,
  isStoredSelectionStale,
  resolveEffectiveAccount as resolveEffectiveAccountPure,
  type MyAccount,
} from "../../../../packages/shared/src/account-context.ts";

export { MY_ACCOUNTS_COLUMNS, fetchMyAccounts, formatAccountRoleLabel, formatWorkspacePickerLabel } from "../../../../packages/shared/src/account-context.ts";
export type { MyAccount } from "../../../../packages/shared/src/account-context.ts";

/** Read the locally persisted workspace selection (null when absent/unavailable). */
export function readSelectedAccount(): string | null {
  try {
    return window.localStorage?.getItem(ACCOUNT_SELECTION_KEY) ?? null;
  } catch {
    return null;
  }
}

/** Persist the workspace selection locally (best effort - storage may be unavailable). */
export function persistSelectedAccount(accountId: string): void {
  try {
    window.localStorage?.setItem(ACCOUNT_SELECTION_KEY, accountId);
  } catch {
    // Selection simply won't survive a reload - the config default takes over.
  }
}

/** Drop the persisted selection (e.g. when it no longer matches a membership). */
export function clearSelectedAccount(): void {
  try {
    window.localStorage?.removeItem(ACCOUNT_SELECTION_KEY);
  } catch {
    // Nothing to clean if storage is unavailable.
  }
}

/**
 * Resolve the effective account using the shared pure resolver, with console's local
 * side effect preserved: a stored selection that no longer matches a membership is dropped
 * from localStorage so it can't resurface. Resolution priority is owned by @secops/shared.
 */
export function resolveEffectiveAccount(
  configAccountId: string,
  accounts: readonly MyAccount[],
  storedSelection: string | null,
): string {
  if (isStoredSelectionStale(accounts, storedSelection)) {
    clearSelectedAccount();
  }
  return resolveEffectiveAccountPure(configAccountId, accounts, storedSelection);
}
